import { DoChatAction } from "../types/Chat";

export const DIALONCE_CLOSE_PAYLOAD = { action: DoChatAction.close } as const;

export type DialonceClosePayload = typeof DIALONCE_CLOSE_PAYLOAD;

type StringMessageBridge = {
  postMessage: (message: string) => void;
};

type WebkitMessageHandler = {
  postMessage: (message: DialonceClosePayload) => void;
};

export type HostCloseWindow = Window & {
  ReactNativeWebView?: StringMessageBridge;
  webkit?: {
    messageHandlers?: {
      dialonce?: WebkitMessageHandler;
    };
  };
  DialonceNative?: StringMessageBridge;
};

const tryNotify = (notify: () => void): boolean => {
  try {
    notify();
    return true;
  } catch {
    return false;
  }
};

/**
 * Notify the embedding host that the chat should close.
 *
 * Tries native WebView bridges first (React Native, iOS WKWebView, Android),
 * then falls back to window.parent.postMessage for iframe / widget embeds.
 *
 * React Native and Android receive a JSON string (`@JavascriptInterface` only
 * accepts primitives/String). iOS WKWebView receives the object payload.
 */
export const notifyHostClose = (): void => {
  if (typeof window === "undefined") return;

  const win = window as HostCloseWindow;
  const payload = DIALONCE_CLOSE_PAYLOAD;
  const payloadJson = JSON.stringify(payload);
  let notifiedNative = false;

  const reactNativeBridge = win.ReactNativeWebView;
  if (reactNativeBridge?.postMessage) {
    if (tryNotify(() => reactNativeBridge.postMessage(payloadJson))) {
      notifiedNative = true;
    }
  }

  const iosBridge = win.webkit?.messageHandlers?.dialonce;
  if (iosBridge?.postMessage) {
    if (tryNotify(() => iosBridge.postMessage(payload))) {
      notifiedNative = true;
    }
  }

  const androidBridge = win.DialonceNative;
  if (androidBridge?.postMessage) {
    if (tryNotify(() => androidBridge.postMessage(payloadJson))) {
      notifiedNative = true;
    }
  }

  // iframe / widget fallback — always emit so web embeds keep working
  tryNotify(() => win.parent.postMessage(payload, "*"));

  if (!notifiedNative && win.parent === win && process.env["NODE_ENV"] !== "production") {
    // eslint-disable-next-line no-console
    console.debug(
      "[dialonce] notifyHostClose: no native bridge found; parent postMessage may be a no-op in top-level WebView"
    );
  }
};
